import { useLocation } from '@reach/router'
import Fuse from 'fuse.js'
import { navigate } from 'gatsby'
import { isEqual, omit, uniqBy } from 'lodash'
import { useContext, useEffect, useState } from 'react'

import { ProductFiltersProps } from '../components/ProductFilters'
import { LocaleContext } from '../locale/i18n'
import { staticPagePaths } from '../locale/pathing'
import {
  industryOrMaterialValues,
  IndustryOrMaterial,
  TypeOfTesting,
  typeOfTestingValues,
} from '../logic/catalog'
import {
  FilterableProduct,
  ProductFiltersByType,
  filterProducts,
  freeTextFilter,
  productFilterFuseOptions,
} from '../logic/productFiltering'
import { ProductFilterType } from '../logic/shared-types'
import { StaticPage } from '../logic/staticPage'
import { clearQueryParameters, updateQueryParameter } from '../logic/url'

import useIsClient from './useIsClient'

const productPageRegex = new RegExp(
  `(?:${Object.values(staticPagePaths[StaticPage.PRODUCTS]).join('|')})$`,
)

export const useFilteredProducts = ({
  products,
}: {
  products: FilterableProduct[]
}) => {
  const location = useLocation()
  const isClient = useIsClient()
  const query = new URLSearchParams(isClient ? location.search : {})
  const { locale } = useContext(LocaleContext)
  const [fuse] = useState(
    () => new Fuse<FilterableProduct>(products, productFilterFuseOptions),
  )
  const deviceTypes = products.flatMap(p => p.deviceTypes)
  const standards = products.flatMap(p => p.standards)

  // support for categoryLandings: /products/c/:industry, /products/c/:testingType
  const categoryFromPath = /\/c\/([\w-]+)\/?(?:\?.+)?$/.exec(
    location.pathname,
  )?.[1]

  const validIndustryFilterValue: IndustryOrMaterial | null =
    industryOrMaterialValues.find(
      c => c === categoryFromPath || c === query?.get('industry'),
    ) ?? null

  const validTypeOfTestingFilterValue: TypeOfTesting | null =
    typeOfTestingValues.find(
      c =>
        String(c) === categoryFromPath ||
        String(c) === query?.get('testingType'),
    ) ?? null

  const queryDeviceType = query?.get('type')
  const validDeviceTypeFilterValue =
    deviceTypes.find(
      dt =>
        dt.deviceTypeId === queryDeviceType ||
        (dt.parentDeviceTypeId && dt.parentDeviceTypeId === queryDeviceType),
    )?.deviceTypeId ?? null

  const validStandardFilterValue = standards.find(
    s => s.slug === query?.get('standard'),
  )

  const textFilterValue = query?.get('q') ?? ''
  const locationFilters: ProductFiltersByType = {
    [ProductFilterType.INDUSTRY_OR_MATERIAL]: {
      type: ProductFilterType.INDUSTRY_OR_MATERIAL,
      value: validIndustryFilterValue,
    },
    [ProductFilterType.TYPE_OF_TESTING]: {
      type: ProductFilterType.TYPE_OF_TESTING,
      value: validTypeOfTestingFilterValue,
    },
    [ProductFilterType.DEVICE_TYPE]: {
      type: ProductFilterType.DEVICE_TYPE,
      value: validDeviceTypeFilterValue,
    },
    [ProductFilterType.TEXT]: freeTextFilter(textFilterValue),
    [ProductFilterType.STANDARD]: {
      type: ProductFilterType.STANDARD,
      value: validStandardFilterValue?.slug ?? null,
    },
  }

  const [filters, setFilters] =
    useState<typeof locationFilters>(locationFilters)

  useEffect(() => {
    if (isClient && !isEqual(locationFilters, filters)) {
      setFilters(locationFilters)
    }
  }, [location.search, isClient])

  useEffect(() => {
    // navigates back to the /products page
    // if category filter has been changed from the initial landing url
    if (location.pathname.match(productPageRegex)) {
      return
    }
    const selectedIndustry = filters.industryOrMaterial
    const selectedTOT = filters.typeOfTesting
    if (
      (selectedIndustry.value && selectedIndustry.value === categoryFromPath) ||
      (selectedTOT.value && selectedTOT.value === categoryFromPath)
    ) {
      return
    }
    const queryParams = new URLSearchParams()
    if (filters.industryOrMaterial.value) {
      queryParams.set('industry', filters.industryOrMaterial.value)
    }
    if (filters.typeOfTesting.value) {
      queryParams.set('testingType', filters.typeOfTesting.value)
    }

    if (filters.text.value) {
      queryParams.set('q', filters.text.value)
    }
    void navigate(
      `${
        staticPagePaths[StaticPage.PRODUCTS][locale]
      }?${queryParams.toString()}`,
    )
  }, [filters])

  const availableIndustries = Array.from(
    new Set([
      ...filterProducts({
        filters: omit(filters, 'industryOrMaterial', 'text'),
        fuse,
        products,
      }).flatMap(({ industryOrMaterial }) => industryOrMaterial),
      ...(validIndustryFilterValue ? [validIndustryFilterValue] : []),
    ]),
  )
  const availableTypesOfTesting = Array.from(
    new Set([
      ...filterProducts({
        filters: omit(filters, 'typeOfTesting', 'text'),
        fuse,
        products,
      }).flatMap(({ typeOfTesting }) => typeOfTesting),
      ...(validTypeOfTestingFilterValue ? [validTypeOfTestingFilterValue] : []),
    ]),
  )
  const availableDeviceTypes = Array.from(
    new Set([
      ...filterProducts({
        filters: omit(filters, 'deviceType', 'text'),
        fuse,
        products,
      }).flatMap(p =>
        p.deviceTypes.map(d => d.parentDeviceTypeId ?? d.deviceTypeId),
      ),
      ...(validDeviceTypeFilterValue ? [validDeviceTypeFilterValue] : []),
    ]),
  )
  const availableStandards = uniqBy(
    [
      ...filterProducts({
        filters: omit(filters, 'standard', 'text'),
        fuse,
        products,
      }).flatMap(p => p.standards),
      ...(validStandardFilterValue ? [validStandardFilterValue] : []),
    ],
    'id',
  )

  const handleChange: ProductFiltersProps['onChange'] = {
    [ProductFilterType.INDUSTRY_OR_MATERIAL]: value => {
      setFilters({
        ...filters,
        industryOrMaterial: {
          type: ProductFilterType.INDUSTRY_OR_MATERIAL,
          value,
        },
      })
      if (value && value !== categoryFromPath) {
        void updateQueryParameter('industry', value)
      } else {
        clearQueryParameters(['industry'])
      }
    },
    [ProductFilterType.TYPE_OF_TESTING]: value => {
      setFilters({
        ...filters,
        typeOfTesting: {
          type: ProductFilterType.TYPE_OF_TESTING,
          value,
        },
      })
      if (value && value !== categoryFromPath) {
        void updateQueryParameter('testingType', value)
      } else {
        clearQueryParameters(['testingType'])
      }
    },
    [ProductFilterType.DEVICE_TYPE]: value => {
      setFilters({
        ...filters,
        deviceType: {
          type: ProductFilterType.DEVICE_TYPE,
          value,
        },
      })
      if (value) {
        void updateQueryParameter('type', value)
      } else {
        clearQueryParameters(['type'])
      }
    },
    [ProductFilterType.STANDARD]: value => {
      setFilters({
        ...filters,
        standard: {
          type: ProductFilterType.STANDARD,
          value,
        },
      })
      if (value) {
        void updateQueryParameter('standard', value)
      } else {
        clearQueryParameters(['standard'])
      }
    },
    [ProductFilterType.TEXT]: value => {
      setFilters({ ...filters, text: freeTextFilter(value) })
      void updateQueryParameter('q', value)
    },
  }

  const handleClear: () => void = () => {
    setFilters({
      ...filters,
      [ProductFilterType.INDUSTRY_OR_MATERIAL]: {
        type: ProductFilterType.INDUSTRY_OR_MATERIAL,
        value: null,
      },

      [ProductFilterType.DEVICE_TYPE]: {
        type: ProductFilterType.DEVICE_TYPE,
        value: null,
      },
      [ProductFilterType.TYPE_OF_TESTING]: {
        type: ProductFilterType.TYPE_OF_TESTING,
        value: null,
      },
      [ProductFilterType.STANDARD]: {
        type: ProductFilterType.STANDARD,
        value: null,
      },
    })
    void clearQueryParameters(['type', 'industry', 'testingType', 'standard'])
  }

  return {
    availableFilters: {
      deviceType: availableDeviceTypes,
      industryOrMaterial: availableIndustries,
      standard: availableStandards,
      typeOfTesting: availableTypesOfTesting,
    },
    filters,
    handleChange,
    handleClear,
    products: filterProducts({ filters, fuse, products }),
  }
}
